import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TeacherLive, { type TopicItem } from "../../components/ai-tutor/TeacherLive";
import {
  COMMUNICATION_SKILLS_LESSON_NO,
  getCanDosWithTopics,
  type CommunicationTurnResponse,
} from "../../services/api/canDo";
import {
  getStudentMe,
  resolveStudentGrade,
  type RosterStudent,
} from "../../modules/roster/api/roster.client";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import { handleSchoolFeatureDisabledFromAxiosError } from "../../utils/schoolFeatureDisabledStudent";
import { practiceTopicsForCommunicationTutor } from "../../utils/canDoTopic";
import { getCommunicationBadgesMe } from "../../services/api/communicationLearning";

const DRAFT_KEY = "tkm_comm_tutor_text_draft";

export default function CommunicationTutorPage() {
  const navigate = useNavigate();
  const { refetch: refetchFeatureAccess } = useFeatureAccess();
  const [searchParams] = useSearchParams();
  const [topics, setTopics] = useState<TopicItem[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [lastInsight, setLastInsight] = useState<CommunicationTurnResponse | null>(null);
  const [turnCount, setTurnCount] = useState(0);
  const [badges, setBadges] = useState<{ badge_key?: string }[]>([]);

  const loadTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const me = await getStudentMe();
      const g = resolveStudentGrade(me as RosterStudent);
      if (g == null) {
        setTopics([]);
        return;
      }
      const { canDos, communicationTopics } = await getCanDosWithTopics(g, COMMUNICATION_SKILLS_LESSON_NO);
      const rows = practiceTopicsForCommunicationTutor({
        communication_topics: communicationTopics,
        can_dos: canDos,
      });
      setTopics(
        rows.map((r) => ({
          topic_id: r.id,
          title: r.title,
          description: r.description,
          practiceCanDoId: r.practiceCanDoId,
          id: r.id,
        }))
      );
      try {
        const b = await getCommunicationBadgesMe();
        setBadges((b.items as { badge_key?: string }[]) ?? []);
      } catch {
        setBadges([]);
      }
    } catch (err) {
      if (
        await handleSchoolFeatureDisabledFromAxiosError(err, navigate, refetchFeatureAccess, "communication_tutor")
      ) {
        return;
      }
      setTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  }, [navigate, refetchFeatureAccess]);

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw && import.meta.env.DEV) {
        /* reserved for typed practice draft */
        void raw;
      }
    } catch {
      /* ignore */
    }
  }, []);

  const perf = lastInsight?.performance;

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      {/* <div className="shrink-0 border-b border-purple-100 bg-gradient-to-r from-violet-50 via-purple-50 to-indigo-50 px-3 py-2">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-2 items-stretch">
          <div className="flex-1 min-w-[120px] rounded-xl bg-white/90 shadow-sm border border-purple-100 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-purple-600 font-semibold">Turns (session)</p>
            <p className="text-lg font-bold text-slate-900">{turnCount}</p>
          </div>
          <div className="flex-1 min-w-[120px] rounded-xl bg-white/90 shadow-sm border border-purple-100 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-purple-600 font-semibold">Fluency</p>
            <p className="text-lg font-bold text-slate-900">{perf ? perf.fluency.toFixed(2) : "—"}</p>
          </div>
          <div className="flex-1 min-w-[120px] rounded-xl bg-white/90 shadow-sm border border-purple-100 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-purple-600 font-semibold">Accuracy</p>
            <p className="text-lg font-bold text-slate-900">{perf ? perf.accuracy.toFixed(2) : "—"}</p>
          </div>
          <div className="flex-1 min-w-[120px] rounded-xl bg-white/90 shadow-sm border border-purple-100 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-purple-600 font-semibold">Appropriateness</p>
            <p className="text-lg font-bold text-slate-900">{perf ? perf.appropriateness.toFixed(2) : "—"}</p>
          </div>
          <div className="flex-[2] min-w-[160px] rounded-xl bg-white/90 shadow-sm border border-purple-100 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-purple-600 font-semibold">Badges</p>
            <p className="text-xs text-slate-700 line-clamp-2">
              {badges.length ? badges.map((b) => b.badge_key ?? "").filter(Boolean).join(", ") : "Keep practicing!"}
            </p>
          </div>
        </div>
      </div> */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <TeacherLive
          onEnd={() => window.history.back()}
          tutorType="communication_tutor"
          showLessonsPanel={false}
          showTopicsPanel={true}
          topics={topics}
          topicsLoading={topicsLoading}
          initialTopicId={searchParams.get("topic_id") ?? undefined}
          communicationLessonNo={COMMUNICATION_SKILLS_LESSON_NO}
          onCommunicationTurnComplete={(res) => {
            setLastInsight(res);
            setTurnCount((c) => c + 1);
            void getCommunicationBadgesMe()
              .then((b) => setBadges((b.items as { badge_key?: string }[]) ?? []))
              .catch(() => {});
          }}
        />
      </div>
    </div>
  );
}
